<app-nav></app-nav>

<main>
  <!-- ─── Hero ─────────────────────────────────────────────────────── -->
  <section class="hero brand-section">
    <div class="hero-orb hero-orb-blue"></div>
    <div class="hero-orb hero-orb-green"></div>

    <div class="hero-inner">
      <div class="hero-text">
        <span class="brand-eyebrow">Find a clean bathroom · in seconds</span>
        <h1>Where to go when you gotta go.</h1>
        <p class="brand-lede">
          Skip To My Loo finds the nearest clean, real-people-approved bathroom
          before the next cramp, road trip stop, or post-coffee crisis.
          Open the app, glance at the map, and walk in like a local.
        </p>
        <div class="hero-ctas">
          <button type="button" class="brand-btn brand-btn-primary" (click)="downloadApp()">
            <mat-icon>download</mat-icon>
            Download the app
          </button>
          <button type="button" class="brand-btn brand-btn-ghost" (click)="scrollToFeatures()">
            See how it works
          </button>
        </div>
        <ul class="hero-trust">
          <li><mat-icon>verified</mat-icon> Verified by neighbors</li>
          <li><mat-icon>accessibility</mat-icon> Accessibility filters</li>
          <li><mat-icon>schedule</mat-icon> 24-hour spots</li>
        </ul>
      </div>

      <!-- Phone mockup, pure CSS. -->
      <div class="hero-mockup" aria-hidden="true">
        <div class="phone">
          <div class="phone-notch"></div>
          <div class="phone-screen">
            <div class="phone-statusbar">
              <span>9:41</span>
              <span class="phone-statusbar-icons">
                <mat-icon>signal_cellular_alt</mat-icon>
                <mat-icon>battery_full</mat-icon>
              </span>
            </div>

            <!--
              Real map screenshot used as the phone's map view. Pin positions
              below are tuned to drop on top of actual landmarks visible in
              the image (Crunch Fitness area for the user dot).
            -->
            <div class="phone-map">
              <img
                class="phone-map-image"
                src="/IMG_1497.jpg"
                alt=""
                aria-hidden="true"
              />
              <!-- The user's "you are here" location dot. -->
              <div class="pin pin-me"><div class="pin-me-pulse"></div></div>
            </div>

            <!--
              Bottom sheet — icons + colours match the pins above. Each row
              shows only attributes the real app actually surfaces:
              distance, 24-hour flag, accessibility, and paid/free.
              No invented descriptors, no fake star ratings.
            -->
            <div class="phone-sheet">
              <div class="phone-sheet-handle"></div>
              <div class="phone-sheet-row">
                <div class="phone-sheet-icon icon-excellent">
                  <mat-icon>directions_walk</mat-icon>
                </div>
                <div class="phone-sheet-copy">
                  <strong>Riverside Public Library</strong>
                  <span>0.2 mi · 24 hours · Accessible</span>
                </div>
                <span class="phone-sheet-tag">Verified</span>
              </div>
              <div class="phone-sheet-row">
                <div class="phone-sheet-icon icon-good">
                  <mat-icon>restaurant</mat-icon>
                </div>
                <div class="phone-sheet-copy">
                  <strong>Café Lumière</strong>
                  <span>0.4 mi · Free · Accessible</span>
                </div>
                <span class="phone-sheet-tag">Verified</span>
              </div>
              <div class="phone-sheet-row">
                <div class="phone-sheet-icon icon-okay">
                  <mat-icon>local_gas_station</mat-icon>
                </div>
                <div class="phone-sheet-copy">
                  <strong>Mobil On The Run</strong>
                  <span>0.6 mi · 24 hours</span>
                </div>
                <span class="phone-sheet-tag">Verified</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Floating chips around the phone (kept brand-true, no fake numbers) -->
        <div class="float-chip chip-1">
          <mat-icon>verified</mat-icon>
          Verified spots
        </div>
        <div class="float-chip chip-2">
          <mat-icon>flash_on</mat-icon>
          Glance and go
        </div>
        <div class="float-chip chip-3">
          <mat-icon>shield</mat-icon>
          Reviewer-trusted
        </div>
      </div>
    </div>
  </section>

  <!-- ─── Promises strip (replaces the fake stats) ─────────────────── -->
  <section class="promises-strip">
    <div class="promises-inner">
      @for (p of promises; track p.title) {
        <div class="promise">
          <div class="promise-icon"><mat-icon>{{ p.icon }}</mat-icon></div>
          <div class="promise-copy">
            <strong>{{ p.title }}</strong>
            <span>{{ p.subtitle }}</span>
          </div>
        </div>
      }
    </div>
  </section>

  <!-- ─── Features ─────────────────────────────────────────────────── -->
  <section id="features" class="features brand-section">
    <div class="section-header">
      <span class="brand-eyebrow">What's inside</span>
      <h2>Everything you need before the moment hits.</h2>
      <p class="brand-lede">
        We obsess over the small things so you don't have to think when it
        matters most.
      </p>
    </div>

    <div class="features-grid">
      @for (feature of features; track feature.title) {
        <article class="feature-card" [attr.data-color]="feature.color">
          <div class="feature-icon">
            <mat-icon>{{ feature.icon }}</mat-icon>
          </div>
          <h3>{{ feature.title }}</h3>
          <p>{{ feature.description }}</p>
        </article>
      }
    </div>
  </section>

  <!-- ─── How it works ─────────────────────────────────────────────── -->
  <section class="how-it-works brand-section">
    <div class="section-header">
      <span class="brand-eyebrow">How it works</span>
      <h2>Three taps and you're walking in.</h2>
    </div>

    <ol class="steps">
      <li class="step">
        <div class="step-num">1</div>
        <h3>Open the map</h3>
        <p>We pin every clean, real-world bathroom near you and color them by rating at a glance.</p>
      </li>
      <li class="step">
        <div class="step-num">2</div>
        <h3>Tap the closest one</h3>
        <p>See hours, accessibility, and a friendly summary of what real people thought.</p>
      </li>
      <li class="step">
        <div class="step-num">3</div>
        <h3>Take me there</h3>
        <p>One tap hands off to Apple Maps or Google Maps for turn-by-turn directions.</p>
      </li>
    </ol>
  </section>

  <!-- ─── Games band ───────────────────────────────────────────────── -->
  <section id="games" class="games-band">
    <div class="games-band-inner">
      <div class="games-band-copy">
        <span class="brand-eyebrow brand-eyebrow-on-dark">A nice surprise</span>
        <h2>Sometimes you wait. Make the waiting fun.</h2>
        <p>
          Twenty-seven small games, designed for the in-between moments. Earn
          tokens, chase a weekly tournament, or just kill three minutes without
          opening another social app.
        </p>
        <button
          type="button"
          class="brand-btn brand-btn-primary brand-btn-on-dark"
          (click)="toggleGamesLineup()"
          [attr.aria-expanded]="gamesOpen"
        >
          <mat-icon>{{ gamesOpen ? 'expand_less' : 'videogame_asset' }}</mat-icon>
          {{ gamesOpen ? 'Hide the lineup' : 'Peek at the lineup' }}
        </button>
      </div>

      <div class="games-band-visual" aria-hidden="true">
        <div class="game-tile tile-1"><mat-icon>sports_esports</mat-icon></div>
        <div class="game-tile tile-2"><mat-icon>extension</mat-icon></div>
        <div class="game-tile tile-3"><mat-icon>casino</mat-icon></div>
        <div class="game-tile tile-4"><mat-icon>flag</mat-icon></div>
        <div class="game-tile tile-5"><mat-icon>emoji_events</mat-icon></div>
        <div class="game-tile tile-6"><mat-icon>bolt</mat-icon></div>
      </div>
    </div>

    <!-- Real lineup, expanded by the button above. Uses an Angular animation
         class so the panel slides in/out without a layout shift. -->
    @if (gamesOpen) {
      <div class="games-lineup" role="list">
        @for (g of gamesLineup; track g.title) {
          <div class="lineup-card" role="listitem">
            <div class="lineup-icon"><mat-icon>{{ g.icon }}</mat-icon></div>
            <span class="lineup-name">{{ g.title }}</span>
          </div>
        }
        <div class="lineup-card lineup-card-more" role="listitem">
          <div class="lineup-icon"><mat-icon>more_horiz</mat-icon></div>
          <span class="lineup-name">+ 15 more</span>
        </div>
      </div>
    }
  </section>

  <!-- ─── Final CTA ────────────────────────────────────────────────── -->
  <section class="final-cta brand-section">
    <div class="final-cta-inner">
      <span class="brand-eyebrow">Ready when you are</span>
      <h2>Join the people who never get caught short.</h2>
      <p class="brand-lede">
        It's free to download, free to find a bathroom, and free to forget the
        next public-restroom panic ever happened.
      </p>
      <div class="hero-ctas final-ctas">
        <button type="button" class="brand-btn brand-btn-primary brand-btn-large" (click)="downloadApp()">
          <mat-icon>smartphone</mat-icon>
          Get the app
        </button>
        <span class="store-coming">Coming soon to iOS &amp; Android.</span>
      </div>
    </div>
  </section>
</main>

<app-footer></app-footer>
